import { EditRounded, MoreVert } from '@mui/icons-material'
import { Box, Button, Card, Container, Grid, IconButton, Menu, MenuItem, Tab, Tabs, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { deleteBrandLine, getBrands, showBrand } from '../../../../../redux/actions/brands'
import { deleteTestimonial, getTestimonials, showTestimonial } from '../../../../../redux/actions/testimonials'
import { IRootState } from '../../../../../redux/types'
import LinesTable from '../../../../frontoffice/library/ui/components/LinesTable'
import CustomSnackBar from '../../../../shared/components/CustomSnackBar'
import Header from '../../../../shared/components/Header'
import ScreenLoading from '../../../../shared/components/ScreenLoading'
import TabPanel from '../../../../shared/components/TabPanel'
import ButtonBack from '../../../../shared/components/buttons/ButtonBack'
import SubHeader from '../../../../shared/styles/Subheader'
import { CardColor, Hero } from '../../styles/BrandsStyle'
import BrandsModal from '../components/BrandModal'
import { DeleteLineDialog } from '../components/DeleteLineModal'
import TestimonialModal from '../components/TestimonialModal'
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid'
import CustomToolbar from '@/contexts/shared/components/CustomToolbar'

function a11yProps(index: any) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`
  }
}

function CoursesView() {
  const { t } = useTranslation(['global'])
  const dispatch = useDispatch()
  const { brandSelected, showForm, error, msg, loading } = useSelector((state: IRootState) => state.brand)
  const { testimonials } = useSelector((state: IRootState) => state.testimonial)
  const [openDeleteLine, setOpenDeleteLine] = useState(false)
  const [lineSelected, _setLineSelected] = useState(false)
  const [anchorOp, setAnchorOp] = useState<null | HTMLElement>(null)
  const [openOptions, setOpenOptions] = useState(-1)

  useEffect(() => {
    dispatch(getBrands())
  }, [])

  useEffect(() => {
    if (brandSelected !== null) dispatch(getTestimonials(brandSelected.id))
  }, [brandSelected])

  const [currentTab, setCurrentTab] = useState(0)

  const handleCloseOptions = () => {
    setAnchorOp(null)
    setOpenOptions(-1)
  }

  const handleChangeTab = (_: unknown, newValue: number) => {
    setCurrentTab(newValue)
  }

  const testimonialsColumns: GridColDef[] = [
    {
      field: 'title',
      headerName: t('title'),
      flex: 1
    },
    {
      field: 'actions',
      headerName: '',
      sortable: false,
      filterable: false,
      flex: 0.5,
      renderCell: (params: GridRenderCellParams) => {
        const rowData = params.row

        return (
          <>
            <IconButton
              aria-controls="simple-menu"
              aria-haspopup="true"
              onClick={event => {
                setAnchorOp(event.currentTarget)
                setOpenOptions(rowData.id)
              }}
            >
              <MoreVert />
            </IconButton>
            <Menu
              id="simple-menu"
              anchorEl={anchorOp}
              keepMounted
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              open={rowData.id === openOptions}
              onClose={handleCloseOptions}
            >
              <MenuItem onClick={() => dispatch(showTestimonial(rowData.id))}>{t('edit')}</MenuItem>
              <MenuItem onClick={() => dispatch(deleteTestimonial(rowData.id))}>{t('delete')}</MenuItem>
            </Menu>
          </>
        )
      }
    }
  ]
  return (
    <>
      {showForm && <BrandsModal />}
      <Header title={t('brands')} />
      <SubHeader />
      {brandSelected !== null && (
        <Box style={{ display: showForm ? 'none' : 'block' }}>
          <Container maxWidth="md">
            <Box mt={6} pr={6} pl={6} mb={3}>
              <ButtonBack />
              <Hero
                mt={6}
                py={2}
                px={6}
                mb={3}
                display="flex"
                alignItems="flex-end"
                style={{
                  background: `linear-gradient(180deg, rgba(250, 250, 250, 0) 31.82%, #FAFAFA 119.72%), url(${`${import.meta.env.VITE_AWS_URL}/brands/${brandSelected.background}`})`
                }}
              >
                <Grid container justifyContent="space-between" spacing={3}>
                  <Grid item>
                    <Typography variant="h3">{brandSelected.name}</Typography>
                  </Grid>
                  <Grid item>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => dispatch(showBrand(brandSelected.id))}
                      startIcon={<EditRounded />}
                    >
                      {t('edit')}
                    </Button>
                  </Grid>
                </Grid>
              </Hero>
            </Box>
            <Card>
              <Box pr={4} pl={4}>
                <Box mt={4} mb={6}>
                  <Tabs
                    value={currentTab}
                    indicatorColor="secondary"
                    textColor="primary"
                    onChange={handleChangeTab}
                    aria-label="disabled tabs example"
                  >
                    <Tab label={t('information')} {...a11yProps(0)} />
                    <Tab label={t('lines')} {...a11yProps(1)} />
                    <Tab label={t('testimonials')} {...a11yProps(2)} />
                    <Tab label={t('photos')} {...a11yProps(3)} />
                  </Tabs>
                </Box>
                <TabPanel value={currentTab} index={0}>
                  <Grid container alignItems="center" justifyContent="center" spacing={4}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle1">{`Logo ${t('light')}`}</Typography>
                      <Box p={3} display="flex" justifyContent="center" my={2} borderRadius={8} style={{ backgroundColor: '#171A1C' }}>
                        <img
                          src={`${import.meta.env.VITE_AWS_URL}/brands/${brandSelected.light_logo}`}
                          width="100%"
                          height="auto"
                          alt="light logo"
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle1">{`Logo ${t('dark')}`}</Typography>
                      <Box p={3} display="flex" justifyContent="center" my={2} borderRadius={8}>
                        <img
                          src={`${import.meta.env.VITE_AWS_URL}/brands/${brandSelected.dark_logo}`}
                          width="100%"
                          height="auto"
                          alt="Logo dark"
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <CardColor>
                        <Box component={'div'} p={1} gap={16} width="160px">
                          <Typography variant="body1">{`Color ${t('main')}`}</Typography>
                          <Typography variant="caption">{brandSelected.main_color}</Typography>
                        </Box>
                        <div
                          style={{
                            backgroundColor: `${brandSelected.main_color}`,
                            width: '80px',
                            borderRadius: '0px 8px 8px 0px'
                          }}
                        />
                      </CardColor>
                      <Box mt={2}>
                        <CardColor>
                          <Box p={1} component={'div'} gap={16} width="160px">
                            <Typography variant="body1">{`Color ${t('secondary')}`}</Typography>
                            <Typography variant="caption">{brandSelected.secondary_color}</Typography>
                          </Box>
                          <div
                            style={{
                              backgroundColor: `${brandSelected.secondary_color}`,
                              width: '80px',
                              borderRadius: '0px 8px 8px 0px'
                            }}
                          />
                        </CardColor>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box display="grid" component={'div'} gap={16}>
                        <Typography variant="subtitle1">{t('description')}</Typography>
                        <Typography variant="body1">{brandSelected.description}</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </TabPanel>
                <TabPanel value={currentTab} index={1}>
                  <LinesTable />
                </TabPanel>

                <TabPanel value={currentTab} index={2}>
                  <TestimonialModal type={0} />
                  <Box className="table_custom" height={500}>
                    <DataGrid
                      rows={testimonials.filter((t: any) => t.type === 0)}
                      columns={testimonialsColumns}
                      getRowId={row => row.id}
                      disableRowSelectionOnClick
                      pageSizeOptions={[10, 20, 50]}
                      initialState={{ pagination: { paginationModel: { page: 0, pageSize: 10 } } }}
                      slots={{
                        toolbar: CustomToolbar
                      }}
                      slotProps={{
                        toolbar: {
                          showQuickFilter: true,
                          quickFilterProps: { debounceMs: 500 }
                        }
                      }}
                    />
                  </Box>
                </TabPanel>
                <TabPanel value={currentTab} index={3}>
                  <TestimonialModal type={1} />
                  <Box className="table_custom" height={500}>
                    <DataGrid
                      rows={testimonials.filter((t: any) => t.type === 1)}
                      columns={testimonialsColumns}
                      getRowId={row => row.id}
                      disableRowSelectionOnClick
                      pageSizeOptions={[10, 20, 50]}
                      initialState={{ pagination: { paginationModel: { page: 0, pageSize: 10 } } }}
                      slots={{
                        toolbar: CustomToolbar
                      }}
                      slotProps={{
                        toolbar: {
                          showQuickFilter: true,
                          quickFilterProps: { debounceMs: 500 }
                        }
                      }}
                    />
                  </Box>
                </TabPanel>
              </Box>
            </Card>
          </Container>
        </Box>
      )}
      <DeleteLineDialog
        open={openDeleteLine}
        onClose={() => setOpenDeleteLine(false)}
        onDelete={() => {
          dispatch(deleteBrandLine(lineSelected))
          setOpenDeleteLine(false)
        }}
        t={t}
      />
      <CustomSnackBar errors={[error]} msgs={[msg]} />
      <ScreenLoading loadings={[loading]} />
    </>
  )
}
export default CoursesView
